import Decimal from 'decimal.js';
import type { OfferTypeValue } from '../../../../store/offer-store';
import type { IRowData } from './OfferTable';

export type OfferInlineStrategy = {
  /** Dado um novo preço digitado, retorna o desconto calculado. Retorna null se não aplicável. */
  priceToDiscount: (price: number, rowData: IRowData) => number | null;
  /** Dado um novo desconto digitado, retorna o preço calculado. Retorna null se não aplicável. */
  discountToPrice: (discount: number, rowData: IRowData) => number | null;
};

const stub = (): null => null;

/** (Preço reg. − Preço oferta) ÷ Preço reg. × 100  — 1 casa decimal */
const derivedDiscount = (price: number, rowData: IRowData): number | null => {
  const reg = rowData.retail_price;
  if (!reg) return null;
  const regDec = new Decimal(reg);
  if (regDec.isZero()) return null;
  return regDec.minus(price).dividedBy(regDec).times(100).toDecimalPlaces(1).toNumber();
};

/** Preço reg. × (1 − desconto% ÷ 100)  — 2 casas decimais */
const derivedPrice = (discount: number, rowData: IRowData): number | null => {
  const reg = rowData.retail_price;
  if (!reg) return null;
  return new Decimal(reg).times(new Decimal(100).minus(discount)).dividedBy(100).toDecimalPlaces(2).toNumber();
};

export const offerInlineEditStrategies: Record<OfferTypeValue, OfferInlineStrategy> = {
  // Grupo: edita preço → calcula desconto
  SIMPLE_PRICE: { priceToDiscount: derivedDiscount, discountToPrice: stub },
  FROM_TO: { priceToDiscount: derivedDiscount, discountToPrice: stub },
  BULK_PRICE: { priceToDiscount: derivedDiscount, discountToPrice: stub },

  // Grupo: edita desconto → calcula preço
  SIMPLE_PERCENTAGE: { priceToDiscount: stub, discountToPrice: derivedPrice },
  BUY_GET_DISCOUNT: { priceToDiscount: stub, discountToPrice: derivedPrice },
  BULK_PERCENTAGE: { priceToDiscount: stub, discountToPrice: derivedPrice },

  // Desconto na 2ª un: (reg × (qty−1) + reg × (1 − d/100)) ÷ qty  — 2 casas decimais
  UNIT_DISCOUNT: {
    priceToDiscount: stub,
    discountToPrice: (discount, rowData) => {
      const reg = rowData.retail_price;
      const qty = rowData.offer_dto?.offer?.buy_quantity ?? 2;
      if (!reg) return null;
      const regDec = new Decimal(reg);
      const qtyDec = new Decimal(qty);
      const discountedUnit = regDec.times(new Decimal(100).minus(discount)).dividedBy(100);
      return regDec.times(qtyDec.minus(1)).plus(discountedUnit).dividedBy(qtyDec).toDecimalPlaces(2).toNumber();
    },
  },

  // Nenhum dos dois é editável — preço é calculado por qtd levada/paga
  BUY_PAY_QUANTITY: { priceToDiscount: stub, discountToPrice: stub },
};
